import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaUser, 
  FaBell, 
  FaShieldAlt, 
  FaPalette, 
  FaGlobe,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaTrash,
  FaDownload
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  color: #ccc;
  font-size: 18px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: 24px;
`;

const SettingsSection = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #333;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIcon = styled.div`
  color: #4CAF50;
  font-size: 24px;
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.div`
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const SettingDescription = styled.div`
  color: #ccc;
  font-size: 14px;
  line-height: 1.4;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #4CAF50;
  }

  &:checked + span:before {
    transform: translateX(26px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #333;
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const Select = styled.select`
  background-color: #222;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Input = styled.input`
  background-color: #222;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  padding: 8px 12px;
  font-size: 14px;
  width: 200px;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &::placeholder {
    color: #666;
  }
`;

const Button = styled.button`
  background-color: ${props => props.danger ? '#dc3545' : '#4CAF50'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${props => props.danger ? '#c82333' : '#45a049'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const DangerZone = styled.div`
  background-color: #1a1a1a;
  border: 1px solid #dc3545;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
`;

const DangerTitle = styled.h3`
  color: #dc3545;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState({
    // Profile settings
    displayName: user?.meta?.name || '',
    bio: user?.meta?.bio || '',
    email: user?.email || '',
    
    // Notification settings
    emailNotifications: true,
    pushNotifications: true,
    newFollowerNotifications: true,
    likeNotifications: true,
    commentNotifications: true,
    
    // Privacy settings
    profileVisibility: 'public',
    showEmail: false,
    allowMessages: true,
    
    // Appearance settings
    theme: 'dark',
    language: 'en',
    fontSize: 'medium',
    
    // Security settings
    twoFactorAuth: false,
    loginAlerts: true
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = () => {
    // Here you would typically save to API
    toast.success('Settings saved successfully!');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      toast.success('Account deletion requested');
    }
  };

  const handleExportData = () => {
    toast.success('Data export started. You will receive an email when ready.');
  };

  return (
    <Container>
      <Content>
        <Header>
          <Title>Settings</Title>
          <Subtitle>
            Manage your account settings, privacy preferences, and notification options.
          </Subtitle>
        </Header>

        <SettingsGrid>
          {/* Profile Settings */}
          <SettingsSection>
            <SectionHeader>
              <SectionIcon>
                <FaUser />
              </SectionIcon>
              <SectionTitle>Profile Settings</SectionTitle>
            </SectionHeader>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Display Name</SettingLabel>
                <SettingDescription>Your name as it appears to other users</SettingDescription>
              </SettingInfo>
              <Input
                value={settings.displayName}
                onChange={(e) => handleSettingChange('displayName', e.target.value)}
                placeholder="Enter display name"
              />
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Bio</SettingLabel>
                <SettingDescription>A short description about yourself</SettingDescription>
              </SettingInfo>
              <Input
                value={settings.bio}
                onChange={(e) => handleSettingChange('bio', e.target.value)}
                placeholder="Enter bio"
              />
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Email</SettingLabel>
                <SettingDescription>Your email address for account notifications</SettingDescription>
              </SettingInfo>
              <Input
                type="email"
                value={settings.email}
                onChange={(e) => handleSettingChange('email', e.target.value)}
                placeholder="Enter email"
              />
            </SettingItem>
          </SettingsSection>

          {/* Notification Settings */}
          <SettingsSection>
            <SectionHeader>
              <SectionIcon>
                <FaBell />
              </SectionIcon>
              <SectionTitle>Notification Settings</SectionTitle>
            </SectionHeader>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Email Notifications</SettingLabel>
                <SettingDescription>Receive notifications via email</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Push Notifications</SettingLabel>
                <SettingDescription>Receive push notifications in your browser</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.pushNotifications}
                  onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>New Follower Notifications</SettingLabel>
                <SettingDescription>Get notified when someone follows you</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.newFollowerNotifications}
                  onChange={(e) => handleSettingChange('newFollowerNotifications', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Like Notifications</SettingLabel>
                <SettingDescription>Get notified when someone likes your posts</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.likeNotifications}
                  onChange={(e) => handleSettingChange('likeNotifications', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>
          </SettingsSection>

          {/* Privacy Settings */}
          <SettingsSection>
            <SectionHeader>
              <SectionIcon>
                <FaShieldAlt />
              </SectionIcon>
              <SectionTitle>Privacy Settings</SectionTitle>
            </SectionHeader>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Profile Visibility</SettingLabel>
                <SettingDescription>Who can see your profile</SettingDescription>
              </SettingInfo>
              <Select
                value={settings.profileVisibility}
                onChange={(e) => handleSettingChange('profileVisibility', e.target.value)}
              >
                <option value="public">Public</option>
                <option value="followers">Followers Only</option>
                <option value="private">Private</option>
              </Select>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Show Email</SettingLabel>
                <SettingDescription>Allow others to see your email address</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.showEmail}
                  onChange={(e) => handleSettingChange('showEmail', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Allow Messages</SettingLabel>
                <SettingDescription>Allow other users to send you messages</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.allowMessages}
                  onChange={(e) => handleSettingChange('allowMessages', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>
          </SettingsSection>

          {/* Appearance Settings */}
          <SettingsSection>
            <SectionHeader>
              <SectionIcon>
                <FaPalette />
              </SectionIcon>
              <SectionTitle>Appearance</SectionTitle>
            </SectionHeader>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Theme</SettingLabel>
                <SettingDescription>Choose your preferred theme</SettingDescription>
              </SettingInfo>
              <Select
                value={settings.theme}
                onChange={(e) => handleSettingChange('theme', e.target.value)}
              >
                <option value="dark">Dark</option>
                <option value="light">Light</option>
                <option value="auto">Auto</option>
              </Select>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Language</SettingLabel>
                <SettingDescription>Choose your preferred language</SettingDescription>
              </SettingInfo>
              <Select
                value={settings.language}
                onChange={(e) => handleSettingChange('language', e.target.value)}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
              </Select>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Font Size</SettingLabel>
                <SettingDescription>Adjust the text size</SettingDescription>
              </SettingInfo>
              <Select
                value={settings.fontSize}
                onChange={(e) => handleSettingChange('fontSize', e.target.value)}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </Select>
            </SettingItem>
          </SettingsSection>

          {/* Security Settings */}
          <SettingsSection>
            <SectionHeader>
              <SectionIcon>
                <FaShieldAlt />
              </SectionIcon>
              <SectionTitle>Security</SectionTitle>
            </SectionHeader>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Two-Factor Authentication</SettingLabel>
                <SettingDescription>Add an extra layer of security to your account</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.twoFactorAuth}
                  onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Login Alerts</SettingLabel>
                <SettingDescription>Get notified of new login attempts</SettingDescription>
              </SettingInfo>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={settings.loginAlerts}
                  onChange={(e) => handleSettingChange('loginAlerts', e.target.checked)}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </SettingItem>
          </SettingsSection>

          {/* Data Management */}
          <SettingsSection>
            <SectionHeader>
              <SectionIcon>
                <FaDownload />
              </SectionIcon>
              <SectionTitle>Data Management</SectionTitle>
            </SectionHeader>

            <SettingItem>
              <SettingInfo>
                <SettingLabel>Export Data</SettingLabel>
                <SettingDescription>Download a copy of your data</SettingDescription>
              </SettingInfo>
              <Button onClick={handleExportData}>
                <FaDownload />
                Export
              </Button>
            </SettingItem>
          </SettingsSection>

          {/* Danger Zone */}
          <DangerZone>
            <DangerTitle>
              <FaTrash />
              Danger Zone
            </DangerTitle>
            <SettingItem>
              <SettingInfo>
                <SettingLabel>Delete Account</SettingLabel>
                <SettingDescription>Permanently delete your account and all associated data</SettingDescription>
              </SettingInfo>
              <Button danger onClick={handleDeleteAccount}>
                <FaTrash />
                Delete Account
              </Button>
            </SettingItem>
          </DangerZone>
        </SettingsGrid>

        <ButtonGroup>
          <Button onClick={handleSaveSettings}>
            <FaSave />
            Save Settings
          </Button>
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default SettingsPage; 